// ── Profile.js — production-ready user profile ──────────────────────────
// Tabs: profile | ratings | invite | settings
// All writes debounced → Supabase (user_core, user_profiles, user_scores)
// Photos → Supabase Storage
// Founding member badge when is_founding_member = true

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTr }          from '../utils/useTr';
import { useApp }         from '../contexts/AppContext';
import { dbUpdate }       from '../utils/supabase';
import { uploadProfilePhoto, compressImage } from '../utils/supabaseStorage';
import { logoutUser }     from '../utils/supabaseAuth';
import { SpotifyConnectButton } from '../components/SpotifyConnect';
import ProfileSheet from '../components/ProfileSheet';
import VerifyIdentity from '../components/VerifyIdentity';
import { maskPhone } from '../utils/verification';
import FriendRadar, { FriendRadarLegend } from '../components/FriendRadar';
import { CompletionBanner }     from '../components/ProfileCompletion';
import { JourneyProgress, RoleBadge } from '../components/Gamification';
import FoundingBadge from '../components/FoundingBadge';
import BeeCell from '../components/BeeCell';
import { getBadges, getPillars } from '../utils/badges';
import { generateBio, getBioQuestions } from '../utils/bioGenerator';
import {
  getJourneyStage, computeRole, computeHiveScore, hiveScoreTier,
  getCurrentSeason, getCurrentWeeklyQuestion, getBeeModsArr,
} from '../utils/beeJourney';
import {
  HiveIcon,
  StageRegisteredIcon, StageScoredIcon, StageKnownIcon,
  StageConnectedIcon, StageHiveIcon, StageLovedIcon,
  MoodGrowingIcon, MoodExploringIcon, MoodReadyIcon,
  MoodConnectingIcon, MoodLowkeyIcon,
  RoleQueenIcon, RoleConnectorIcon, RoleBuilderIcon, RoleScoutIcon,
} from '../components/icons';

// ── Debounce helper ───────────────────────────────────────────────────────
function useDebounce(fn, delay = 800) {
  const timer = useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

// ── Interest categories (matches quiz interests) ───────────────────────────
const INTEREST_GROUPS = [
  { label:'Food & Drink',   keys:['interest.coffee','interest.wine','interest.cooking','interest.restaurants','interest.baking'] },
  { label:'Arts & Culture', keys:['interest.music','interest.cinema','interest.art','interest.theatre','interest.photography','interest.design'] },
  { label:'Outdoors',       keys:['interest.hiking','interest.cycling','interest.running','interest.yoga','interest.travel','interest.camping'] },
  { label:'Mind',           keys:['interest.reading','interest.podcasts','interest.philosophy','interest.languages','interest.tech','interest.gaming'] },
  { label:'Social',         keys:['interest.dancing','interest.volunteering','interest.sports','interest.fitness','interest.meditation'] },
];

// ── Tab bar ───────────────────────────────────────────────────────────────
function TabBar({ tab, setTab, tr, features }) {
  // Entourage tab requires friend_rating (the mechanic that powers it).
  // Settings is always a core tab — no feature flag gate.
  const showEntourage = features?.friend_rating !== false;
  const tabs = [
    { id:'profile',  label: tr('nav.profile') || 'Profile' },
    { id:'hive',     label: 'Hive' },
    ...(showEntourage ? [{ id:'amis',     label: 'Entourage' }] : []),
    { id:'settings', label: tr('common.settings') || 'Settings' },
  ];
  return (
    <div style={{ display:'flex', borderBottom:'1px solid var(--border)', background:'var(--black)', position:'sticky', top:0, zIndex:10 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => setTab(t.id)} style={{
          flex:1, padding:'0.65rem 0.2rem', border:'none', borderBottom:`2px solid ${tab===t.id?'var(--gold)':'transparent'}`,
          background:'transparent', color:tab===t.id?'var(--gold)':'var(--muted)',
          fontSize:'0.72rem', fontWeight:tab===t.id?600:400, cursor:'pointer', transition:'all 0.15s',
        }}>{t.label}</button>
      ))}
    </div>
  );
}

// ── Photo upload grid ──────────────────────────────────────────────────────
// Up to MAX_PHOTOS slots are storable, but the UI only renders the filled ones
// plus a single "add" tile that shows how many slots remain.
const MAX_PHOTOS = 6;
function PhotoGrid({ photos, onAdd, onRemove, uploading, lang }) {
  const fr = lang === 'fr';
  const filled = (photos || []).filter(Boolean);
  const remaining = Math.max(0, MAX_PHOTOS - filled.length);
  const nextIdx = filled.length;       // index where the new photo will land
  const inputId = `photo-input-${nextIdx}`;
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.4rem', marginBottom:'1.2rem' }}>
      {filled.map((url, i) => (
        <div key={`p-${i}`} style={{ aspectRatio:'3/4', borderRadius:'var(--r-md)', overflow:'hidden', background:'var(--charcoal)', border:'1px solid var(--border)', position:'relative' }}>
          <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          <button onClick={e => { e.stopPropagation(); onRemove(i); }}
            style={{ position:'absolute', top:4, right:4, width:22, height:22, borderRadius:'50%', background:'rgba(0,0,0,0.7)', border:'none', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12 }}>✕</button>
        </div>
      ))}

      {remaining > 0 && (
        <div
          onClick={() => document.getElementById(inputId)?.click()}
          style={{
            aspectRatio:'3/4', borderRadius:'var(--r-md)', overflow:'hidden',
            background:'var(--charcoal)', border:'1px dashed var(--border)',
            position:'relative', cursor:'pointer',
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6,
          }}
        >
          {uploading === nextIdx
            ? <div style={{ width:22, height:22, border:'2px solid var(--gold)', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
            : <>
                <span style={{ fontSize:'1.6rem', color:'var(--gold)', opacity:0.65, lineHeight:1 }}>+</span>
                <span style={{ fontSize:'0.62rem', color:'var(--muted)', textAlign:'center', lineHeight:1.4, padding:'0 6px' }}>
                  {fr
                    ? `${remaining} place${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''}`
                    : `${remaining} slot${remaining > 1 ? 's' : ''} left`}
                </span>
              </>}
          <input id={inputId} type="file" accept="image/*" style={{ display:'none' }}
            onChange={e => e.target.files?.[0] && onAdd(nextIdx, e.target.files[0])}/>
        </div>
      )}
    </div>
  );
}

// ── Interest picker ────────────────────────────────────────────────────────
function InterestPicker({ selected, onChange, tr }) {
  return (
    <div style={{ marginBottom:'1.2rem' }}>
      <p style={{ fontSize:'0.68rem', fontWeight:600, letterSpacing:'0.12em', color:'var(--muted)', textTransform:'uppercase', marginBottom:'0.6rem' }}>
        {tr('profile.interests_label') || 'Interests'}
      </p>
      {INTEREST_GROUPS.map(group => (
        <div key={group.label} style={{ marginBottom:'0.6rem' }}>
          <p style={{ fontSize:'0.65rem', color:'var(--muted)', marginBottom:'0.3rem' }}>{group.label}</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem' }}>
            {group.keys.map(key => {
              const on = (selected||[]).includes(key);
              const label = key.replace('interest.','').replace(/_/g,' ');
              return (
                <button key={key} onClick={() => {
                  const next = on ? (selected||[]).filter(k=>k!==key) : [...(selected||[]),key];
                  onChange(next);
                }} style={{
                  padding:'0.25rem 0.65rem', borderRadius:'99px', border:`1px solid ${on?'var(--gold)':'var(--border)'}`,
                  background:on?'var(--gold-pale)':'transparent', color:on?'var(--gold)':'var(--muted)',
                  fontSize:'0.7rem', cursor:'pointer', transition:'all 0.12s', fontFamily:'Outfit',
                }}>{label}</button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Field row ─────────────────────────────────────────────────────────────
function FieldRow({ label, children }) {
  return (
    <div style={{ marginBottom:'0.9rem' }}>
      <p style={{ fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.1em', color:'var(--muted)', textTransform:'uppercase', marginBottom:'0.3rem' }}>{label}</p>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, multiline }) {
  const props = { value: value||'', onChange: e => onChange(e.target.value), placeholder,
    style:{ width:'100%', padding:'0.6rem 0.75rem', background:'rgba(255,255,255,0.04)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.85rem', outline:'none', resize:'vertical', boxSizing:'border-box' }};
  return multiline ? <textarea rows={3} {...props}/> : <input {...props}/>;
}

// ── SELECT helper ─────────────────────────────────────────────────────────
function SelectRow({ label, value, onChange, options }) {
  return (
    <FieldRow label={label}>
      <select value={value||''} onChange={e => onChange(e.target.value)} style={{ width:'100%', padding:'0.6rem 0.75rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:value?'var(--white)':'var(--muted)', fontSize:'0.82rem', outline:'none', cursor:'pointer', fontFamily:'Outfit' }}>
        <option value="">—</option>
        {options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </FieldRow>
  );
}

// ── About Me — option lists ────────────────────────────────────────────────
const EDU_OPTS       = [['highschool','High school'],['some-college','Some college'],['bachelors',"Bachelor's"],['masters',"Master's"],['phd','PhD'],['trade','Trade school'],['other','Other']];
const REL_GOAL_OPTS  = [['relationship','Relationship'],['casual','Casual'],['friendship','Friendship'],['marriage','Marriage'],['unsure','Not sure yet']];
const LOVE_LANG_OPTS = [['words','Words of affirmation'],['acts','Acts of service'],['gifts','Gift giving'],['quality','Quality time'],['touch','Physical touch']];
const DRINKING_OPTS  = [['never','Never'],['socially','Socially'],['regularly','Regularly']];
const SMOKING_OPTS   = [['never','Never'],['socially','Socially'],['daily','Daily']];
const EXERCISE_OPTS  = [['daily','Every day'],['3-4x/week','3–4×/week'],['1-2x/week','1–2×/week'],['rarely','Rarely'],['never','Never']];
const DIET_OPTS      = [['omnivore','Omnivore'],['vegetarian','Vegetarian'],['vegan','Vegan'],['halal','Halal'],['kosher','Kosher'],['other','Other']];
const SLEEP_OPTS     = [['early','Early bird'],['night','Night owl'],['flexible','Flexible']];
const RELIGION_OPTS  = [['agnostic','Agnostic'],['atheist','Atheist'],['christian','Christian'],['jewish','Jewish'],['muslim','Muslim'],['hindu','Hindu'],['buddhist','Buddhist'],['spiritual','Spiritual'],['other','Other']];

// ── Section header with optional action button ────────────────────────────
function SectionHead({ txt, btn }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', margin:'1.4rem 0 0.3rem' }}>
      <p style={{ margin:0, fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.14em', color:'var(--muted)', textTransform:'uppercase' }}>{txt}</p>
      {btn}
    </div>
  );
}

// ── Detail row: display-first, click pencil to edit, visibility badge ─────
function ProfileDetailRow({ label, value, displayValue, visible, onSave, onToggleVis, type='text', options=[], placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value || '');
  useEffect(() => setDraft(value || ''), [value]);
  const shown = displayValue || (options.find(([v]) => v === value)?.[1]) || value || '';
  const commit = (v) => {
    const val = v !== undefined ? v : draft;
    if (val !== value) onSave(val);
    setEditing(false);
  };
  return (
    <div style={{ display:'flex', alignItems:'center', minHeight:38, padding:'0.25rem 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
      <span style={{ flex:'0 0 36%', fontSize:'0.7rem', color:'var(--gold)', paddingRight:8, lineHeight:1.5 }}>{label}</span>
      <div style={{ flex:1, display:'flex', alignItems:'center', gap:6, minWidth:0 }}>
        {editing
          ? type === 'select'
              ? <select autoFocus value={draft} onChange={e => commit(e.target.value)} onBlur={() => setEditing(false)}
                  style={{ flex:1, background:'var(--charcoal)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-sm)', color:'var(--white)', fontSize:'0.78rem', padding:'3px 6px', fontFamily:'Outfit', outline:'none' }}>
                  <option value="">—</option>
                  {options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              : <input autoFocus type={type} value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onBlur={() => commit()}
                  onKeyDown={e => { if (e.key==='Enter') commit(); if (e.key==='Escape') { setDraft(value||''); setEditing(false); } }}
                  placeholder={placeholder||'—'}
                  style={{ flex:1, background:'transparent', border:'none', borderBottom:'1px solid var(--gold)', color:'var(--white)', fontSize:'0.82rem', fontFamily:'Outfit', outline:'none', padding:'2px 0' }}/>
          : <>
              <span style={{ flex:1, fontSize:'0.82rem', color:shown?'var(--white)':'var(--muted)', fontStyle:shown?'normal':'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {shown || '—'}
              </span>
              <button onClick={() => setEditing(true)} title="Edit" style={{ background:'none', border:'none', cursor:'pointer', padding:3, color:'var(--muted)', opacity:0.5, flexShrink:0 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
            </>
        }
      </div>
      <button onClick={onToggleVis} style={{ flexShrink:0, marginLeft:8, padding:'3px 8px', borderRadius:99, border:`1px solid ${visible?'rgba(201,168,76,0.4)':'rgba(255,255,255,0.1)'}`, background:visible?'rgba(201,168,76,0.08)':'transparent', color:visible?'var(--gold)':'var(--muted)', fontSize:'0.58rem', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', cursor:'pointer', display:'flex', alignItems:'center', gap:3, whiteSpace:'nowrap' }}>
        {visible && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
        {visible ? 'Visible' : 'Hidden'}
      </button>
    </div>
  );
}

// ── Suggested prompts per question slot ──────────────────────────────────
const PROMPT_SUGGESTIONS = [
  [
    "Two truths and a lie — guess which is the lie:",
    "The thing most people get wrong about me:",
    "I'll know we're compatible if:",
    "The last thing that made me genuinely laugh:",
    "My love language in practice looks like:",
    "A non-negotiable in a relationship:",
  ],
  [
    "I'm weirdly passionate about:",
    "The best decision I ever made:",
    "My ideal Sunday looks like:",
    "Something I'm working on right now:",
    "The most spontaneous thing I've done:",
    "What I'm looking for, honestly:",
  ],
  [
    "A green flag I always notice:",
    "I feel most like myself when:",
    "What I bring to a relationship:",
    "My friends would describe me as:",
    "A question I'd want you to ask me:",
    "Something I changed my mind about:",
  ],
];

function PromptInput({ q, setQ, a, setA, qKey, aKey, index, debouncedSaveProfile }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestions = PROMPT_SUGGESTIONS[index] || [];
  return (
    <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.75rem', marginBottom:'0.5rem' }}>
      <div style={{ position:'relative' }}>
        <input value={q} onChange={e => { setQ(e.target.value); debouncedSaveProfile({ [qKey]: e.target.value }); }}
          onFocus={() => !q && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder="Add a question…"
          style={{ display:'block', width:'100%', background:'transparent', border:'none', borderBottom:q?'none':'1px dashed rgba(255,255,255,0.1)', color:'var(--gold)', fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:'Outfit', outline:'none', marginBottom:'0.45rem', padding:0, boxSizing:'border-box' }}/>
        {!q && showSuggestions && (
          <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:10, background:'var(--charcoal)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', overflow:'hidden', boxShadow:'0 4px 16px rgba(0,0,0,0.4)' }}>
            {suggestions.map((s, si) => (
              <div key={si}
                onMouseDown={() => { setQ(s); debouncedSaveProfile({ [qKey]: s }); setShowSuggestions(false); }}
                style={{ padding:'0.45rem 0.65rem', fontSize:'0.7rem', color:'var(--white)', cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,0.05)', lineHeight:1.4 }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(201,168,76,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                {s}
              </div>
            ))}
          </div>
        )}
      </div>
      <textarea value={a} rows={2}
        onChange={e => { setA(e.target.value); debouncedSaveProfile({ [aKey]: e.target.value }); }}
        placeholder="Your answer…"
        style={{ display:'block', width:'100%', background:'transparent', border:'none', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.85rem', lineHeight:1.6, outline:'none', resize:'none', padding:0, boxSizing:'border-box' }}/>
    </div>
  );
}

// ── Bottom sheet overlay ──────────────────────────────────────────────────
function BottomSheet({ title, onClose, children }) {
  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:600, background:'rgba(0,0,0,0.72)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}
      onClick={onClose}
    >
      <div
        style={{ background:'var(--dark)', borderTop:'1px solid var(--border)', borderRadius:'14px 14px 0 0', maxWidth:480, width:'100%', margin:'0 auto', maxHeight:'88vh', display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.1rem 0.6rem', borderBottom:'1px solid rgba(255,255,255,0.05)', flexShrink:0 }}>
          <span style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.13em', textTransform:'uppercase', color:'var(--gold)' }}>{title}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.1rem', lineHeight:1, padding:4 }}>✕</button>
        </div>
        <div style={{ overflowY:'auto', padding:'0.8rem 1.1rem 2.5rem', flex:1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Profile tab (merged with About) ───────────────────────────────────────
function ProfileTab({ user, lang, tr, onSaveCore, onSaveProfile, onSavePhotos }) {
  // ── Identity fields ──
  const [photos,    setPhotos]    = useState(user?.photos || []);
  const [name,      setName]      = useState(user?.name || '');
  const [bio,       setBio]       = useState(user?.bio || '');
  const [job,       setJob]       = useState(user?.job || '');
  const [city,      setCity]      = useState(user?.city || '');
  const [interests, setInterests] = useState(user?.interests || []);
  const [uploading,   setUploading]   = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [bioSheet,    setBioSheet]    = useState(false);
  const [bioAnswers,  setBioAnswers]  = useState({});
  const [bioLoading,  setBioLoading]  = useState(false);
  const [editSheet,   setEditSheet]   = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  // editSheet: null | 'about' | 'prompts' | 'details' | 'interests'

  // ── About fields ──
  const [p1q,       setP1q]       = useState(user?.prompt1_q || '');
  const [p1a,       setP1a]       = useState(user?.prompt1_a || '');
  const [p2q,       setP2q]       = useState(user?.prompt2_q || '');
  const [p2a,       setP2a]       = useState(user?.prompt2_a || '');
  const [p3q,       setP3q]       = useState(user?.prompt3_q || '');
  const [p3a,       setP3a]       = useState(user?.prompt3_a || '');
  const [height,    setHeight]    = useState(user?.height?.toString() || '');
  const [education, setEducation] = useState(user?.education || '');
  const [eduField,  setEduField]  = useState(user?.education_field || '');
  const [school,    setSchool]    = useState(user?.school || '');
  const [languages, setLanguages] = useState((user?.languages || []).join(', '));
  const [relGoal,   setRelGoal]   = useState(user?.relationship_goal || '');
  const [loveLang,  setLoveLang]  = useState(user?.love_language || '');
  const [drinking,  setDrinking]  = useState(user?.drinking || '');
  const [smoking,   setSmoking]   = useState(user?.smoking || '');
  const [cannabis,  setCannabis]  = useState(user?.cannabis || '');
  const [exercise,  setExercise]  = useState(user?.exercise || '');
  const [diet,      setDiet]      = useState(user?.diet || '');
  const [pets,      setPets]      = useState(user?.pets || '');
  const [sleep,     setSleep]     = useState(user?.sleep || '');
  const [religion,  setReligion]  = useState(user?.religion || '');
  const [vis,       setVis]       = useState(user?.profile_visibility || {});

  const debouncedSaveCore    = useDebounce((v) => onSaveCore(v));
  const debouncedSaveProfile = useDebounce((v) => onSaveProfile(v));

  const handleName      = (v) => { setName(v);      debouncedSaveCore({ name: v }); };
  const handleBio       = (v) => { setBio(v);       debouncedSaveProfile({ bio: v }); };
  const handleJob       = (v) => { setJob(v);       debouncedSaveProfile({ job: v }); };
  const handleCity      = (v) => { setCity(v);      debouncedSaveProfile({ city: v }); };
  const handleInterests = (v) => { setInterests(v); debouncedSaveProfile({ interests: v }); };

  const isVis    = (k) => vis[k] !== false;
  const toggleVis = (k) => {
    const next = { ...vis, [k]: !isVis(k) };
    setVis(next);
    onSaveProfile({ profile_visibility: next });
  };

  const handleAddPhoto = async (i, file) => {
    setUploadError(null);
    setUploading(i);
    try {
      const compressed = await compressImage(file);
      const { url, error } = await uploadProfilePhoto(user?.id, compressed, i);
      if (error || !url) {
        setUploadError(error || 'Upload failed — check your connection and try again.');
      } else {
        const next = [...(photos||[])];
        next[i] = url;
        setPhotos(next);
        onSavePhotos(next);
      }
    } catch (e) {
      setUploadError('Unexpected error during upload. Please try again.');
    } finally {
      setUploading(null);
    }
  };

  const handleRemovePhoto = (i) => {
    const next = [...(photos||[])];
    next.splice(i, 1);
    setPhotos(next);
    onSavePhotos(next);
  };

  const fr = lang === 'fr';

  const dobDisplay = (() => {
    const { dob_day: d, dob_month: m, dob_year: y } = user || {};
    if (!d || !m || !y) return null;
    try {
      return new Intl.DateTimeFormat(fr ? 'fr-CA' : 'en-CA', { day:'numeric', month:'long', year:'numeric' })
        .format(new Date(parseInt(y), parseInt(m) - 1, parseInt(d)));
    } catch { return null; }
  })();

  // Resolve a stored option key to its display label
  const resolve = (opts, val) => opts?.find(([v]) => v === val)?.[1] || val || '';

  // Detail items — only filled ones, in read order
  const detailItems = [
    height    && { label: fr ? 'Taille'       : 'Height',      text: `${height} cm` },
    education && { label: fr ? 'Études'       : 'Education',   text: resolve(EDU_OPTS, education) },
    eduField  && { label: fr ? 'Domaine'      : 'Field',       text: eduField },
    school    && { label: fr ? 'École'        : 'School',      text: school },
    languages && { label: fr ? 'Langues'      : 'Languages',   text: languages },
    relGoal   && { label: fr ? 'Cherche'      : 'Looking for', text: resolve(REL_GOAL_OPTS, relGoal) },
    loveLang  && { label: fr ? 'Amour'        : 'Love lang.',  text: resolve(LOVE_LANG_OPTS, loveLang) },
    drinking  && { label: fr ? 'Alcool'       : 'Drinking',    text: resolve(DRINKING_OPTS, drinking) },
    smoking   && { label: fr ? 'Tabac'        : 'Smoking',     text: resolve(SMOKING_OPTS, smoking) },
    cannabis  && { label: fr ? 'Cannabis'     : 'Cannabis',    text: resolve(SMOKING_OPTS, cannabis) },
    exercise  && { label: fr ? 'Sport'        : 'Exercise',    text: resolve(EXERCISE_OPTS, exercise) },
    diet      && { label: fr ? 'Alimentation' : 'Diet',        text: resolve(DIET_OPTS, diet) },
    pets      && { label: fr ? 'Animaux'      : 'Pets',        text: pets },
    sleep     && { label: fr ? 'Sommeil'      : 'Sleep',       text: resolve(SLEEP_OPTS, sleep) },
    religion  && { label: fr ? 'Religion'     : 'Religion',    text: resolve(RELIGION_OPTS, religion) },
  ].filter(Boolean);

  const aboutEmpty    = !name && !bio && !job && !city;
  const promptsEmpty  = !((p1q && p1a) || (p2q && p2a) || (p3q && p3a));
  const detailsEmpty  = detailItems.length === 0;
  const interestsEmpty = interests.length === 0;

  // ── Read-view card ──────────────────────────────────────────────────────
  const Card = ({ label, onEdit, isEmpty, emptyMsg, children }) => (
    <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:12, padding:'1rem 1.1rem', marginBottom:'0.65rem' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: isEmpty ? 0 : '0.8rem' }}>
        <span style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.13em', textTransform:'uppercase', color:'var(--gold)' }}>
          {label}
        </span>
        <button onClick={onEdit} style={{ background:'none', border:'none', color:'var(--gold)', fontSize:'0.72rem', cursor:'pointer', fontFamily:'Outfit', opacity:0.75, padding:0 }}>
          {isEmpty ? (fr ? '+ Ajouter' : '+ Add') : (fr ? 'Modifier' : 'Edit')} →
        </button>
      </div>
      {isEmpty
        ? <p style={{ margin:0, fontSize:'0.78rem', color:'var(--muted)', fontStyle:'italic' }}>{emptyMsg}</p>
        : children}
    </div>
  );

  return (
    <div style={{ padding:'1rem 1.1rem 5rem' }}>
      <JourneyProgress user={user} lang={lang}/>

      {/* Preview button — see profile as others do */}
      <button onClick={() => setShowPreview(true)} style={{
        display:'flex', alignItems:'center', gap:'0.5rem', width:'100%', marginBottom:'1rem',
        padding:'0.6rem 1rem', background:'transparent', border:'1px solid var(--border)',
        cursor:'pointer', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.72rem',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>
        </svg>
        {fr ? 'Voir mon profil comme les autres le voient' : 'Preview my profile as others see it'}
      </button>

      {showPreview && (
        <ProfileSheet user={user} onClose={() => setShowPreview(false)} preview={true}/>
      )}

      {/* Photos — always interactive */}
      <PhotoGrid photos={photos} onAdd={handleAddPhoto} onRemove={handleRemovePhoto} uploading={uploading} lang={lang}/>
      {uploadError && (
        <div style={{ background:'rgba(192,69,58,0.12)', border:'1px solid rgba(192,69,58,0.4)', borderRadius:'var(--r-md)', padding:'0.6rem 0.8rem', marginBottom:'0.8rem', fontSize:'0.75rem', color:'#E2785A', display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span>⚠</span><span>{uploadError}</span>
          <button onClick={() => setUploadError(null)} style={{ marginLeft:'auto', background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1rem', lineHeight:1 }}>✕</button>
        </div>
      )}

      {/* ── ABOUT ──────────────────────────────────────────────────────── */}
      <Card
        label={fr ? 'À propos' : 'About'} isEmpty={aboutEmpty}
        emptyMsg={fr ? 'Ajoute ton nom, ta bio et ton métier.' : 'Add your name, bio and job.'}
        onEdit={() => setEditSheet('about')}
      >
        {name && (
          <p style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'1.35rem', fontWeight:400, color:'var(--white)', margin:'0 0 2px', lineHeight:1.2 }}>
            {name}{user?.age ? `, ${user.age}` : ''}
          </p>
        )}
        {(job || city) && (
          <p style={{ fontSize:'0.78rem', color:'var(--muted)', margin:'0 0 10px', lineHeight:1.4 }}>
            {[job, city].filter(Boolean).join(' · ')}
          </p>
        )}
        {bio && (
          <p style={{ fontSize:'0.82rem', lineHeight:1.65, color:'var(--muted)', margin:0, fontStyle:'italic' }}>
            "{bio}"
          </p>
        )}
      </Card>

      {/* ── PROMPTS ────────────────────────────────────────────────────── */}
      <Card
        label="Prompts" isEmpty={promptsEmpty}
        emptyMsg={fr ? 'Ajoute des réponses pour te démarquer.' : 'Add prompts to stand out.'}
        onEdit={() => setEditSheet('prompts')}
      >
        {[[p1q,p1a],[p2q,p2a],[p3q,p3a]].filter(([q,a]) => q && a).map(([q,a], i, arr) => (
          <div key={i} style={{ marginBottom: i < arr.length - 1 ? '0.9rem' : 0 }}>
            <p style={{ margin:'0 0 4px', fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--gold)' }}>{q}</p>
            <p style={{ margin:0, fontSize:'0.82rem', lineHeight:1.6, color:'var(--white)' }}>{a}</p>
          </div>
        ))}
      </Card>

      {/* ── DETAILS ────────────────────────────────────────────────────── */}
      <Card
        label={fr ? 'Détails' : 'Details'} isEmpty={detailsEmpty}
        emptyMsg={fr ? 'Remplis tes détails pour apparaître dans plus de recherches.' : 'Fill in details to appear in more searches.'}
        onEdit={() => setEditSheet('details')}
      >
        <div style={{ display:'flex', flexDirection:'column', gap:'0.45rem' }}>
          {detailItems.map((d, i) => (
            <div key={i} style={{ display:'flex', gap:'0.6rem', fontSize:'0.82rem', lineHeight:1.4 }}>
              <span style={{ minWidth:'4.5rem', flexShrink:0, color:'var(--muted)', fontSize:'0.7rem', paddingTop:'0.07rem' }}>{d.label}</span>
              <span style={{ color:'var(--white)' }}>{d.text}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* ── INTERESTS ──────────────────────────────────────────────────── */}
      <Card
        label={fr ? 'Intérêts' : 'Interests'} isEmpty={interestsEmpty}
        emptyMsg={fr ? 'Tes intérêts apparaîtront ici.' : 'Your interests will appear here.'}
        onEdit={() => setEditSheet('interests')}
      >
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
          {interests.map(k => (
            <span key={k} style={{ padding:'4px 12px', background:'rgba(201,168,76,0.07)', border:'1px solid rgba(201,168,76,0.22)', borderRadius:99, fontSize:'0.72rem', color:'var(--gold)' }}>
              {tr(k) || k.replace('interest.', '')}
            </span>
          ))}
        </div>
      </Card>

      {/* ── SPOTIFY ────────────────────────────────────────────────────── */}
      <SpotifyConnectButton/>

      {/* ── EDIT SHEETS ────────────────────────────────────────────────── */}

      {editSheet === 'about' && (
        <BottomSheet title={fr ? 'À propos' : 'About'} onClose={() => setEditSheet(null)}>
          <FieldRow label={tr('auth.step_name_q') || 'First name'}>
            <TextInput value={name} onChange={handleName} placeholder={fr ? 'Ton prénom' : 'Your name'}/>
          </FieldRow>
          {dobDisplay && (
            <FieldRow label={fr ? 'Date de naissance' : 'Date of birth'}>
              <p style={{ margin:0, fontSize:'0.85rem', color:'var(--white)', opacity:0.75 }}>
                {dobDisplay}
                <span style={{ fontSize:'0.6rem', color:'var(--muted)', marginLeft:'0.5rem' }}>
                  {fr ? '· non modifiable' : '· cannot be changed'}
                </span>
              </p>
            </FieldRow>
          )}
          <div style={{ marginBottom:'0.9rem' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.3rem' }}>
              <p style={{ margin:0, fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.1em', color:'var(--muted)', textTransform:'uppercase' }}>
                {tr('profile.bio_label') || 'Bio'}
              </p>
              <button onClick={() => setBioSheet(true)} style={{ padding:'3px 10px', borderRadius:99, background:'rgba(201,168,76,0.1)', border:'1px solid rgba(201,168,76,0.35)', color:'var(--gold)', fontSize:'0.62rem', fontWeight:600, cursor:'pointer', fontFamily:'Outfit' }}>
                {tr('profile.generate_bio')}
              </button>
            </div>
            <TextInput value={bio} onChange={handleBio} placeholder={fr ? 'Une courte bio…' : 'Write a short bio…'} multiline/>
          </div>
          <FieldRow label={tr('profile.job_label') || 'Job / field'}>
            <TextInput value={job} onChange={handleJob} placeholder={fr ? 'Ex. Designer produit' : 'e.g. Product designer'}/>
          </FieldRow>
          <FieldRow label={tr('profile.city_label') || 'City'}>
            <TextInput value={city} onChange={handleCity} placeholder={fr ? 'Ta ville' : 'Your city'}/>
          </FieldRow>
        </BottomSheet>
      )}

      {editSheet === 'prompts' && (
        <BottomSheet title="Prompts" onClose={() => setEditSheet(null)}>
          <PromptInput q={p1q} setQ={setP1q} a={p1a} setA={setP1a} qKey="prompt1_q" aKey="prompt1_a" index={0} debouncedSaveProfile={debouncedSaveProfile}/>
          <PromptInput q={p2q} setQ={setP2q} a={p2a} setA={setP2a} qKey="prompt2_q" aKey="prompt2_a" index={1} debouncedSaveProfile={debouncedSaveProfile}/>
          <PromptInput q={p3q} setQ={setP3q} a={p3a} setA={setP3a} qKey="prompt3_q" aKey="prompt3_a" index={2} debouncedSaveProfile={debouncedSaveProfile}/>
        </BottomSheet>
      )}

      {editSheet === 'details' && (
        <BottomSheet title={fr ? 'Détails' : 'Details'} onClose={() => setEditSheet(null)}>
          <ProfileDetailRow label="Height" value={height} displayValue={height?`${height} cm`:''} visible={isVis('height')} onToggleVis={() => toggleVis('height')} type="number" placeholder="Height in cm"
            onSave={v => { setHeight(v); onSaveProfile({ height: v ? parseInt(v, 10) : null }); }}/>
          <ProfileDetailRow label="Education" value={education} visible={isVis('education')} onToggleVis={() => toggleVis('education')} type="select" options={EDU_OPTS}
            onSave={v => { setEducation(v); onSaveProfile({ education: v || null }); }}/>
          <ProfileDetailRow label="Field of study" value={eduField} visible={isVis('education_field')} onToggleVis={() => toggleVis('education_field')}
            onSave={v => { setEduField(v); debouncedSaveProfile({ education_field: v }); }}/>
          <ProfileDetailRow label="School" value={school} visible={isVis('school')} onToggleVis={() => toggleVis('school')}
            onSave={v => { setSchool(v); debouncedSaveProfile({ school: v }); }}/>
          <ProfileDetailRow label="Languages" value={languages} visible={isVis('languages')} onToggleVis={() => toggleVis('languages')} placeholder="e.g. French, English"
            onSave={v => { setLanguages(v); onSaveProfile({ languages: v ? v.split(',').map(s => s.trim()).filter(Boolean) : [] }); }}/>
          <ProfileDetailRow label="Looking for" value={relGoal} visible={isVis('relationship_goal')} onToggleVis={() => toggleVis('relationship_goal')} type="select" options={REL_GOAL_OPTS}
            onSave={v => { setRelGoal(v); onSaveProfile({ relationship_goal: v || null }); }}/>
          <ProfileDetailRow label="Love language" value={loveLang} visible={isVis('love_language')} onToggleVis={() => toggleVis('love_language')} type="select" options={LOVE_LANG_OPTS}
            onSave={v => { setLoveLang(v); onSaveProfile({ love_language: v || null }); }}/>
          <ProfileDetailRow label="Drinking" value={drinking} visible={isVis('drinking')} onToggleVis={() => toggleVis('drinking')} type="select" options={DRINKING_OPTS}
            onSave={v => { setDrinking(v); onSaveProfile({ drinking: v || null }); }}/>
          <ProfileDetailRow label="Smoking" value={smoking} visible={isVis('smoking')} onToggleVis={() => toggleVis('smoking')} type="select" options={SMOKING_OPTS}
            onSave={v => { setSmoking(v); onSaveProfile({ smoking: v || null }); }}/>
          <ProfileDetailRow label="Cannabis" value={cannabis} visible={isVis('cannabis')} onToggleVis={() => toggleVis('cannabis')} type="select" options={SMOKING_OPTS}
            onSave={v => { setCannabis(v); onSaveProfile({ cannabis: v || null }); }}/>
          <ProfileDetailRow label="Exercise" value={exercise} visible={isVis('exercise')} onToggleVis={() => toggleVis('exercise')} type="select" options={EXERCISE_OPTS}
            onSave={v => { setExercise(v); onSaveProfile({ exercise: v || null }); }}/>
          <ProfileDetailRow label="Diet" value={diet} visible={isVis('diet')} onToggleVis={() => toggleVis('diet')} type="select" options={DIET_OPTS}
            onSave={v => { setDiet(v); onSaveProfile({ diet: v || null }); }}/>
          <ProfileDetailRow label="Pets" value={pets} visible={isVis('pets')} onToggleVis={() => toggleVis('pets')} placeholder="e.g. Cat, Dog"
            onSave={v => { setPets(v); debouncedSaveProfile({ pets: v }); }}/>
          <ProfileDetailRow label="Sleep" value={sleep} visible={isVis('sleep')} onToggleVis={() => toggleVis('sleep')} type="select" options={SLEEP_OPTS}
            onSave={v => { setSleep(v); onSaveProfile({ sleep: v || null }); }}/>
          <ProfileDetailRow label="Religion" value={religion} visible={isVis('religion')} onToggleVis={() => toggleVis('religion')} type="select" options={RELIGION_OPTS}
            onSave={v => { setReligion(v); onSaveProfile({ religion: v || null }); }}/>
        </BottomSheet>
      )}

      {editSheet === 'interests' && (
        <BottomSheet title={fr ? 'Intérêts' : 'Interests'} onClose={() => setEditSheet(null)}>
          <InterestPicker selected={interests} onChange={handleInterests} tr={tr}/>
        </BottomSheet>
      )}

      {/* Bio generator — z:700 floats above the About sheet (z:600) */}
      {bioSheet && (
        <div style={{ position:'fixed', inset:0, zIndex:700, background:'rgba(0,0,0,0.7)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}
          onClick={() => !bioLoading && setBioSheet(false)}>
          <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderBottom:'none', padding:'1.4rem', maxWidth:480, width:'100%', margin:'0 auto', borderRadius:'var(--r-md) var(--r-md) 0 0' }}
            onClick={e => e.stopPropagation()}>
            <p style={{ margin:'0 0 1rem', fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--gold)' }}>
              {tr('profile.generate_bio')}
            </p>
            {getBioQuestions(lang).map(q => (
              <div key={q.id} style={{ marginBottom:'0.85rem' }}>
                <p style={{ margin:'0 0 0.3rem', fontSize:'0.72rem', color:'var(--white)' }}>{q.emoji} {q.label}</p>
                <textarea value={bioAnswers[q.id] || ''} onChange={e => setBioAnswers(a => ({ ...a, [q.id]: e.target.value }))}
                  placeholder={q.placeholder} rows={2}
                  style={{ width:'100%', padding:'0.5rem 0.7rem', background:'rgba(255,255,255,0.04)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.78rem', resize:'none', outline:'none', boxSizing:'border-box' }}/>
              </div>
            ))}
            <div style={{ display:'flex', gap:'0.6rem', marginTop:'0.5rem' }}>
              <button onClick={() => setBioSheet(false)} disabled={bioLoading} style={{ flex:1, padding:'0.6rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', color:'var(--muted)', fontSize:'0.72rem', cursor:'pointer', fontFamily:'Outfit' }}>
                {tr('common.cancel')}
              </button>
              <button disabled={bioLoading}
                onClick={async () => {
                  setBioLoading(true);
                  const result = await generateBio(bioAnswers, { name, city, job, interests });
                  handleBio(result);
                  setBioLoading(false);
                  setBioSheet(false);
                }}
                style={{ flex:2, padding:'0.6rem', background:'rgba(201,168,76,0.9)', border:'none', borderRadius:'var(--r-sm)', color:'#0A0A0A', fontSize:'0.72rem', fontWeight:700, cursor: bioLoading ? 'wait' : 'pointer', fontFamily:'Outfit' }}>
                {bioLoading ? '…' : tr('profile.generate_bio')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Hive tab — journey, badges, mood, weekly question ─────────────────────
const STAGE_ICONS_MAP = {
  registered: StageRegisteredIcon, scored: StageScoredIcon, known: StageKnownIcon,
  connected: StageConnectedIcon, hive: StageHiveIcon, loved: StageLovedIcon,
};
const MOOD_ICONS_MAP = {
  growing: MoodGrowingIcon, exploring: MoodExploringIcon, ready: MoodReadyIcon,
  connecting: MoodConnectingIcon, lowkey: MoodLowkeyIcon,
};
const ROLE_ICONS_MAP = {
  queen: RoleQueenIcon, queenbee: RoleQueenIcon,
  connector: RoleConnectorIcon, builder: RoleBuilderIcon, scout: RoleScoutIcon,
};

function HiveTab({ user, lang, tr, onSaveCore, onSaveProfile }) {
  const { matches, conversations, setCurrentUser, theme } = useApp();

  const journey  = getJourneyStage(user, conversations || {}, matches || [], lang);
  const role     = computeRole(user, {}, conversations || {}, matches || []);
  const hScore   = computeHiveScore(user, { matches:(matches||[]).length });
  const hTier    = hiveScoreTier(hScore);
  const season   = getCurrentSeason();
  const question = getCurrentWeeklyQuestion(lang);
  const moods    = getBeeModsArr(lang);
  const allBadges= getBadges(lang);
  const pillars  = getPillars(lang);

  const [weeklyInput, setWeeklyInput] = useState(user?.weeklyAnswer || '');
  const [weeklySaved, setWeeklySaved] = useState(!!user?.weeklyAnswer);
  const [hiveTab,     setHiveTab]     = useState('parcours'); // 'parcours' | 'badges' | 'humeur'

  // Analytics: fetch likes received count
  const [likesReceived, setLikesReceived] = useState(null);
  const [streakDays, setStreakDays] = useState(null);
  useEffect(() => {
    if (!user?.id) return;
    import('../utils/supabase').then(({ supabase }) => {
      supabase?.from('likes').select('id', { count:'exact', head:true }).eq('to_user', user.id)
        .then(({ count }) => setLikesReceived(count ?? 0));
    });
    import('../utils/engagement').then(({ getStreak }) => {
      getStreak(user.id).then(s => setStreakDays(s.days));
    });
  }, [user?.id]);

  const vis = user?.profile_visibility || {};
  const isBadgeVisible = id => vis[`badge_${id}`] !== false;
  const toggleBadge = async id => {
    const key  = `badge_${id}`;
    const next = { ...vis };
    if (isBadgeVisible(id)) { next[key] = false; } else { delete next[key]; }
    setCurrentUser(u => ({ ...u, profile_visibility: next }));
    await onSaveProfile({ profile_visibility: next });
  };

  // Compute badge stats from real data
  const convCount = Object.keys(conversations || {}).length;
  const matchCount = (matches || []).length;
  const badgeStats = {
    activeConversationRate: convCount > 0 ? Math.min(1, convCount / Math.max(1, matchCount)) : 0,
    coherenceScore: user?.coherence_score || 0,
  };

  const earned = allBadges.filter(b => b.condition(user, badgeStats));
  const locked = allBadges.filter(b => !b.condition(user, badgeStats));

  const SHead = ({ label }) => (
    <p style={{ fontSize:'0.58rem', letterSpacing:'0.18em', textTransform:'uppercase',
      color:'var(--gold)', margin:'1.4rem 0 0.6rem',
      display:'flex', alignItems:'center', gap:'0.5rem' }}>
      {label}
      <span style={{ flex:1, height:'1px', background:'var(--border)', display:'inline-block' }}/>
    </p>
  );

  const currentMoodId = user?.mood || 'exploring';

  const fr = lang === 'fr';
  const HIVE_TABS = [
    { id:'parcours', label: fr ? 'Parcours' : 'Journey', icon:'◈', color:'var(--gold)', bg:'rgba(201,168,76,0.18)'  },
    { id:'badges',   label: fr ? 'Badges'   : 'Badges',  icon:'★', color:'#A084C4',     bg:'rgba(160,132,196,0.18)' },
    { id:'humeur',   label: fr ? 'Humeur'   : 'Mood',    icon:'♥', color:'#4EC994',     bg:'rgba(78,201,148,0.18)'  },
  ];

  return (
    <div style={{ padding:'1rem 1rem 2rem' }}>

      {/* Sub-tab bar */}
      <div style={{ display:'flex', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:3, marginBottom:'1.1rem' }}>
        {HIVE_TABS.map(t => {
          const active = hiveTab === t.id;
          return (
            <button key={t.id} onClick={() => setHiveTab(t.id)} style={{
              flex:1, padding:'0.45rem 0.3rem',
              background: active ? t.bg : 'transparent',
              border: active ? `1px solid ${t.color}55` : '1px solid transparent',
              borderRadius:'calc(var(--r-md) - 2px)', cursor:'pointer',
              transition:'all 0.15s', fontFamily:'Outfit',
            }}>
              <span style={{ fontSize:'0.58rem', fontWeight: active ? 600 : 400, letterSpacing:'0.06em', textTransform:'uppercase', color: active ? 'var(--white)' : 'var(--muted)', transition:'color 0.15s' }}>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Tab: Parcours ── */}
      {hiveTab === 'parcours' && (<>

        {/* Analytics */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1px', background:'var(--border)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', overflow:'hidden', marginBottom:'0.8rem' }}>
          {[
            { label: fr ? 'Likes reçus' : 'Likes received', value: likesReceived ?? '—' },
            { label: fr ? 'Matchs' : 'Matches',              value: (matches||[]).length },
            { label: fr ? 'Jours actifs' : 'Day streak',     value: streakDays != null ? `${streakDays}` : '—' },
          ].map(({ label, value }) => (
            <div key={label} style={{ background:'var(--dark)', padding:'0.75rem 0.5rem', textAlign:'center' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', color:'var(--gold)', lineHeight:1 }}>{value}</div>
              <div style={{ fontSize:'0.55rem', color:'var(--muted)', marginTop:'0.25rem', letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Hive Score */}
        <div style={{ padding:'1rem', background:hTier.color+'08', border:`1px solid ${hTier.color}22`, marginBottom:'0.9rem' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.5rem' }}>
            <div style={{ fontSize:'0.58rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'var(--muted)' }}>
              {tr('hive.hive_score')}
            </div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:700, color:hTier.color, lineHeight:1 }}>
              {hScore}<span style={{ fontSize:'0.65rem', color:'var(--muted)', fontFamily:'Outfit', fontWeight:400 }}>/100</span>
            </div>
          </div>
          <div style={{ height:3, background:'var(--charcoal)', marginBottom:'0.4rem' }}>
            <div style={{ height:'100%', width:`${hScore}%`, background:`linear-gradient(90deg, ${hTier.color}55, ${hTier.color})`, transition:'width 1s ease' }}/>
          </div>
          <div style={{ fontSize:'0.64rem', color:hTier.color }}>{tr(hTier.labelKey)}</div>
        </div>

        {/* BeeCell + stages */}
        <SHead label={tr('hive.journey_title')}/>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', marginBottom:'0.9rem' }}>
          <div style={{ padding:'1.2rem', background:'var(--charcoal)', border:'1px solid var(--border)', display:'inline-block' }}>
            <BeeCell progress={journey.progress} size={Math.min(220, (typeof window !== 'undefined' ? window.innerWidth : 400) - 80)} animated/>
          </div>
          <div style={{ textAlign:'center', marginTop:'0.6rem' }}>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', color:'var(--gold)', marginBottom:'0.15rem' }}>
              {journey.currentStage ? journey.currentStage.name : (tr('hive.welcome')||'Welcome')}
            </div>
            <div style={{ fontSize:'0.6rem', color:'var(--muted)' }}>
              {journey.completedCount}/6 {tr('hive.stages')} · {6-journey.completedCount} {tr('hive.remaining')}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'1px', background:'rgba(201,168,76,0.04)', border:'1px solid rgba(201,168,76,0.1)', marginBottom:'0.5rem' }}>
          {journey.progress.map(stage => {
            const isNext = journey.nextStage?.id === stage.id;
            const dim    = !stage.completed && !isNext;
            const StIcon = STAGE_ICONS_MAP[stage.icon] || StageRegisteredIcon;
            return (
              <div key={stage.id} style={{ display:'flex', gap:'0.8rem', alignItems:'flex-start', padding:'0.75rem 0.9rem',
                background: stage.completed ? stage.color+'0E' : isNext ? 'rgba(201,168,76,0.03)' : 'transparent',
                borderLeft:`3px solid ${stage.completed ? stage.color : isNext ? 'rgba(201,168,76,0.25)' : 'rgba(255,255,255,0.04)'}`,
                opacity: dim ? 0.4 : 1 }}>
                <div style={{ width:34, height:34, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                  background: stage.completed ? stage.color+'16' : 'rgba(255,255,255,0.02)',
                  border:`1px solid ${stage.completed ? stage.color+'44' : 'rgba(255,255,255,0.05)'}` }}>
                  <StIcon size={15} color={stage.completed ? stage.color : 'rgba(255,255,255,0.2)'}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.12rem', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'0.78rem', fontWeight:600, color: stage.completed ? stage.color : isNext ? 'var(--white)' : 'var(--muted)' }}>{stage.name}</span>
                    {stage.completed && <span style={{ fontSize:'0.48rem', letterSpacing:'0.11em', textTransform:'uppercase', color:stage.color, background:stage.color+'14', padding:'0.06rem 0.3rem' }}>{tr('beehive.unlocked')||'Unlocked'}</span>}
                    {isNext && !stage.completed && <span style={{ fontSize:'0.48rem', letterSpacing:'0.11em', textTransform:'uppercase', color:'var(--gold)', background:'rgba(201,168,76,0.08)', padding:'0.06rem 0.3rem' }}>{tr('hive.next_stage')}</span>}
                  </div>
                  <div style={{ fontSize:'0.64rem', color:'var(--muted)', lineHeight:1.5 }}>{stage.desc}</div>
                  {stage.completed && <div style={{ fontSize:'0.56rem', color:stage.color, marginTop:'0.15rem', fontStyle:'italic' }}>✦ {stage.reward}</div>}
                </div>
                {stage.completed && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={stage.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, marginTop:'0.25rem' }}>
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      </>)}

      {/* ── Tab: Badges ── */}
      {hiveTab === 'badges' && (<>
        <SHead label={tr('hive.my_badges')}/>
        {earned.length > 0 ? (
          <>
            <div style={{ fontSize:'0.61rem', color:'var(--muted)', marginBottom:'0.65rem' }}>
              {earned.length}/{allBadges.length} {tr('hive.obtained')}
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem', marginBottom:'0.9rem' }}>
              {earned.map(b => {
                const col     = pillars[b.pillar]?.color || 'var(--gold)';
                const visible = isBadgeVisible(b.id);
                return (
                  <div key={b.id} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.2rem' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.35rem 0.75rem', background:col+'12', border:`1px solid ${col}44` }}>
                      <div style={{ width:6, height:6, borderRadius:'50%', background:col }}/>
                      <span style={{ fontSize:'0.72rem', color:col, fontWeight:600 }}>{b.name}</span>
                    </div>
                    <button onClick={() => toggleBadge(b.id)} style={{
                      fontSize:'0.46rem', letterSpacing:'0.1em', textTransform:'uppercase',
                      color: visible ? 'var(--gold)' : 'var(--muted2)',
                      background:'none', border:`1px solid ${visible ? 'var(--gold-border)' : 'var(--border)'}`,
                      padding:'0.08rem 0.38rem', cursor:'pointer', fontFamily:'Outfit',
                    }}>
                      {visible ? tr('common.visible') : tr('common.hidden')}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ padding:'1rem', background:'var(--charcoal)', border:'1px solid var(--border)', fontSize:'0.75rem', color:'var(--muted)', textAlign:'center', marginBottom:'0.9rem' }}>
            {tr('hive.no_badges')}
          </div>
        )}
        {locked.length > 0 && (
          <>
            <div style={{ fontSize:'0.6rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.5rem' }}>
              {tr('hive.to_unlock')}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'1px', border:'1px solid var(--border)', marginBottom:'0.5rem' }}>
              {locked.map(b => {
                const col = pillars[b.pillar]?.color || 'var(--muted)';
                return (
                  <div key={b.id} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.65rem 0.85rem', background:'var(--charcoal)', borderLeft:'3px solid var(--border)' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--muted2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'0.77rem', color:'var(--muted)', fontWeight:600 }}>{b.name}</div>
                      <div style={{ fontSize:'0.6rem', color:'var(--muted2)', lineHeight:1.4, marginTop:'0.08rem' }}>{b.desc}</div>
                    </div>
                    <span style={{ fontSize:'0.5rem', padding:'0.1rem 0.4rem', background:col+'12', border:`1px solid ${col}33`, color:col, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap', flexShrink:0 }}>
                      {pillars[b.pillar]?.label || b.pillar}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </>)}

      {/* ── Tab: Humeur ── */}
      {hiveTab === 'humeur' && (<>

        {/* Role + current mood summary */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'0.9rem' }}>
          {(() => {
            const RIcon  = (role && ROLE_ICONS_MAP[role.icon]) || RoleScoutIcon;
            const rawCol = role?.color || 'var(--muted)';
            const col    = (theme === 'light' && rawCol === '#FFD700') ? '#7A5200' : rawCol;
            return (
              <div style={{ padding:'1rem', background:col+'18', border:`1px solid ${col}40`,
                display:'flex', flexDirection:'column', alignItems:'center', gap:'0.5rem', textAlign:'center' }}>
                <RIcon size={28} color={col}/>
                <div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'0.95rem', color:col }}>{role ? role.name : '—'}</div>
                  <div style={{ fontSize:'0.5rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'var(--muted)', marginTop:'0.2rem' }}>{tr('hive.bee_role')}</div>
                </div>
              </div>
            );
          })()}
          {(() => {
            const mObj  = moods.find(m => m.id === currentMoodId) || moods[2];
            const MIcon = MOOD_ICONS_MAP[mObj.id] || MoodReadyIcon;
            return (
              <div style={{ padding:'1rem', background:mObj.color+'10', border:`1px solid ${mObj.color}28`,
                display:'flex', flexDirection:'column', alignItems:'center', gap:'0.5rem', textAlign:'center' }}>
                <MIcon size={28} color={mObj.color}/>
                <div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'0.95rem', color:mObj.color }}>{mObj.label}</div>
                  <div style={{ fontSize:'0.5rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'var(--muted)', marginTop:'0.2rem' }}>{tr('hive.bee_mood')}</div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Mood selector */}
        <SHead label={tr('beehive.bee_mood')}/>
        <p style={{ fontSize:'0.67rem', color:'var(--muted)', marginBottom:'0.7rem', lineHeight:1.6 }}>
          {tr('beehive.mood_visible')}
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
          {moods.map(mood => {
            const active = currentMoodId === mood.id;
            const MIcon  = MOOD_ICONS_MAP[mood.id];
            return (
              <button key={mood.id} onClick={async () => {
                setCurrentUser(u => ({ ...u, mood: mood.id }));
                await onSaveCore({ mood: mood.id });
              }} style={{
                display:'flex', alignItems:'center', gap:'0.7rem', padding:'0.55rem 0.85rem',
                background: active ? mood.color+'18' : 'transparent',
                border:`1px solid ${active ? mood.color+'66' : 'var(--border)'}`,
                cursor:'pointer', transition:'all 0.2s', fontFamily:'Outfit', textAlign:'left',
              }}>
                {MIcon ? <MIcon size={18} color={active ? mood.color : 'var(--muted)'}/> : null}
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'0.75rem', fontWeight:600, color:active ? mood.color : 'var(--white)' }}>{mood.label}</div>
                  <div style={{ fontSize:'0.6rem', color:'var(--muted)', lineHeight:1.3 }}>{mood.desc}</div>
                </div>
                {active && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={mood.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </>)}

    </div>
  );
}

// ── Ratings tab — shows friend-defined axis radar, no scores/names ─────────
function RatingsTab({ user, tr, lang }) {
  const fr  = lang === 'fr';
  const cs  = user?.criteria_scores     || {};
  const csf = user?.criteria_scores_fit || {};
  const hasSeenData  = Object.keys(cs).length  > 0;
  const hasIdealData = Object.keys(csf).length > 0;
  const ratingCount  = user?.votes || 0;
  const [rTab, setRTab] = useState('seen');

  const EmptyRadar = ({ msg }) => (
    <div style={{ textAlign:'center', padding:'2rem 1rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)' }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.2" strokeLinecap="round" style={{ marginBottom:'0.7rem' }}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
      <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.6 }}>{msg}</p>
    </div>
  );

  return (
    <div style={{ padding:'1rem 1.1rem 5rem' }}>

      {/* Sub-tab bar */}
      <div style={{ display:'flex', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:3, marginBottom:'1.2rem' }}>
        {[
          { id:'seen',  label: fr ? 'Comment mes amis me voient' : 'How friends see me' },
          { id:'ideal', label: fr ? 'Personne idéale selon eux' : 'Ideal match for me'  },
        ].map(t => (
          <button key={t.id} onClick={() => setRTab(t.id)} style={{
            flex:1, padding:'0.45rem 0.3rem',
            background: rTab === t.id ? 'var(--charcoal)' : 'transparent',
            border: rTab === t.id ? '1px solid var(--border)' : '1px solid transparent',
            borderRadius:'calc(var(--r-md) - 2px)', cursor:'pointer',
            fontSize:'0.62rem', fontWeight: rTab === t.id ? 600 : 400,
            letterSpacing:'0.04em', color: rTab === t.id ? 'var(--white)' : 'var(--muted)',
            transition:'all 0.15s', fontFamily:'Outfit', lineHeight:1.3,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Tab: how friends see you */}
      {rTab === 'seen' && (
        <div style={{ marginBottom:'1.5rem' }}>
          {hasSeenData ? (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
              <FriendRadar selfScores={cs} needScores={{}} mode="own" size={260}/>
              <FriendRadarLegend mode="own" showNeeds={false}/>
              {ratingCount > 0 && (
                <p style={{ fontSize:'0.65rem', color:'var(--muted)', marginTop:'0.6rem', textAlign:'center' }}>
                  {tr('ratings.based_on') ? tr('ratings.based_on').replace('{n}', ratingCount) : `Based on ${ratingCount} friend${ratingCount > 1 ? 's' : ''}`}
                </p>
              )}
            </div>
          ) : (
            <EmptyRadar msg={tr('ratings.youll_rate') || 'Share your link — friends answer questions about you.'}/>
          )}

          {/* Friend quotes */}
          {(user?.friend_ratings?.quotes || []).length > 0 && (
            <div style={{ marginTop:'1.2rem' }}>
              <p style={{ fontSize:'0.62rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.8rem' }}>
                {tr('ratings.what_they_say') || 'What they say'}
              </p>
              {user.friend_ratings.quotes.map((q, i) => (
                <p key={i} style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', fontSize:'0.95rem', color:'var(--muted)', lineHeight:1.65, borderLeft:'2px solid var(--gold-border)', paddingLeft:'0.8rem', marginBottom:'0.5rem' }}>"{q}"</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: ideal partner as seen by friends */}
      {rTab === 'ideal' && (
        <div style={{ marginBottom:'1.5rem' }}>
          {hasIdealData ? (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
              <FriendRadar selfScores={csf} needScores={{}} mode="own" size={260}/>
              <FriendRadarLegend mode="own" showNeeds={false}/>
              <p style={{ fontSize:'0.65rem', color:'var(--muted)', marginTop:'0.6rem', textAlign:'center', lineHeight:1.6, maxWidth:'22rem' }}>
                {fr ? 'Ce que tes amis pensent que tu recherches chez un partenaire.' : 'What your friends think you need in a partner.'}
              </p>
            </div>
          ) : (
            <EmptyRadar msg={fr ? 'Tes amis n\'ont pas encore défini ton idéal.' : 'Your friends haven\'t defined your ideal match yet.'}/>
          )}
        </div>
      )}

      {/* Invite nudge — always visible */}
      <div style={{ background:'var(--dark)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-lg)', padding:'1rem 1.1rem' }}>
        <p style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--gold)', marginBottom:'0.3rem' }}>
          {tr('invite.how_title') || 'Ask more friends'}
        </p>
        <p style={{ fontSize:'0.75rem', color:'var(--muted)', lineHeight:1.6 }}>
          {tr('about.friends_note') || 'More definitions = more accurate compatibility.'}
        </p>
      </div>
    </div>
  );
}

// ── Invite tab ─────────────────────────────────────────────────────────────
function InviteTab({ user, tr, lang }) {
  const fr = lang === 'fr';
  const [invites,      setInvites]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [email,        setEmail]        = useState('');
  const [sending,      setSending]      = useState(false);
  const [copied,       setCopied]       = useState(null);
  const [sent,         setSent]         = useState(false);
  const [showPreview,  setShowPreview]  = useState(false);

  const loadInvites = useCallback(() => {
    if (!user?.id) return;
    import('../utils/supabase').then(({ supabase }) => {
      supabase?.from('invitations')
        .select('id, code, recipient_email, invite_type, used_count, created_at')
        .eq('created_by', user.id)
        .not('recipient_email', 'is', null)
        .order('created_at', { ascending: false })
        .limit(20)
        .then(({ data }) => { setInvites(data || []); setLoading(false); });
    });
  }, [user?.id]);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const handleSend = async () => {
    const addr = email.trim().toLowerCase();
    if (!addr || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) return;
    setSending(true);
    setSent(false);
    try {
      const origin = window.location.origin || 'https://beelove.me';
      const { supabase } = await import('../utils/supabase');

      // Cancel any pending (unused) invitations to the same address
      const { data: existing } = await supabase
        ?.from('invitations')
        .select('id')
        .eq('created_by', user.id)
        .eq('recipient_email', addr)
        .eq('used_count', 0) || {};
      if (existing?.length) {
        await supabase?.from('invitations').delete().in('id', existing.map(r => r.id));
      }

      const code = crypto.randomUUID();
      const { error } = await supabase?.from('invitations').insert({
        code,
        created_by:      user.id,
        recipient_email: addr,
        invite_type:     'regular',
        granted_role:    'user',
        is_founding:     false,
        max_uses:        1,
        note:            `Sent by ${user.name || user.id} to ${addr}`,
      });
      if (error) { alert(error.message); setSending(false); return; }
      loadInvites();

      const inviteLink = `${origin}/#invite/${code}`;
      const senderName = user?.name || (fr ? 'Quelqu\'un' : 'Someone');

      const subject = fr
        ? `${senderName} t'invite sur BeeLove`
        : `${senderName} invited you to BeeLove`;

      const body = fr
        ? `Salut !\n\n${senderName} t'invite à décrire sa personnalité — pour l'aider à trouver quelqu'un qui lui correspond vraiment.\n\nPas besoin de compte. Pas de rédaction — juste des curseurs. Ça prend quelques minutes.\n\nTon lien :\n${inviteLink}`
        : `Hey!\n\n${senderName} is inviting you to describe their personality — to help them find someone truly compatible.\n\nNo account needed. No writing — just sliders. Takes a few minutes.\n\nYour link:\n${inviteLink}`;

      window.location.href = `mailto:${encodeURIComponent(addr)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      setSent(true);
      setEmail('');
    } finally { setSending(false); }
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/#invite/${code}`;
    navigator.clipboard?.writeText(link).catch(() => {});
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const used  = invites.filter(i => i.used_count > 0).length;
  const total = invites.length;

  return (
    <div style={{ padding:'1rem 1.1rem 5rem' }}>
      {/* Intro */}
      <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem 1.1rem', marginBottom:'1rem' }}>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.15rem', fontWeight:400, color:'var(--white)', margin:'0 0 0.5rem', lineHeight:1.3 }}>
          {fr ? `Laisse un ami te décrire.` : `Let a friend describe you.`}
        </p>
        <p style={{ fontSize:'0.78rem', color:'var(--muted)', margin:0, lineHeight:1.6 }}>
          {fr
            ? `Ton ami glisse des curseurs pour décrire ta personnalité et le partenaire qui t'irait bien — quelqu'un qu'il connaît déjà, pas de rédaction. Ses réponses entrent directement dans ton score de compatibilité.`
            : `Your friend slides scales to describe your personality and the partner who'd suit you — someone they already know, no writing. Their answers feed directly into your compatibility score.`}
        </p>
      </div>

      {/* Send form */}
      <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem' }}>
        <p style={{ fontSize:'0.72rem', fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 0.6rem' }}>
          {fr ? 'Envoyer une invitation par e-mail' : 'Send an invitation by email'}
        </p>
        <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
          <input
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setSent(false); }}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder={fr ? 'ami@email.com' : 'friend@email.com'}
            style={{ flex:1, padding:'0.6rem 0.85rem', background:'var(--surface,#1a1a1a)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--white)', fontSize:'0.85rem', fontFamily:'Outfit', outline:'none' }}
          />
          <button
            onClick={handleSend}
            disabled={sending || !email.trim()}
            style={{ padding:'0.6rem 1.1rem', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', color:'var(--gold)', fontFamily:'Outfit', fontWeight:700, fontSize:'0.82rem', cursor:(!email.trim()||sending)?'default':'pointer', whiteSpace:'nowrap', opacity:(!email.trim())?0.45:1, transition:'opacity 0.15s' }}
          >
            {sending ? '…' : (fr ? 'Envoyer →' : 'Send →')}
          </button>
        </div>
        {sent && (
          <div style={{ marginTop:'0.6rem', padding:'0.5rem 0.7rem', background:'rgba(78,201,148,0.08)', border:'1px solid rgba(78,201,148,0.25)', borderRadius:'var(--r-md)' }}>
            <p style={{ fontSize:'0.72rem', color:'#4EC994', margin:0 }}>
              ✓ {fr ? 'Invitation envoyée.' : 'Invitation sent.'}
            </p>
          </div>
        )}

        {/* Questionnaire format preview */}
        <button
          onClick={() => setShowPreview(v => !v)}
          style={{ background:'none', border:'none', padding:'0.55rem 0 0', color:'var(--muted)', fontSize:'0.7rem', cursor:'pointer', fontFamily:'Outfit', display:'flex', alignItems:'center', gap:'0.3rem' }}
        >
          <span style={{ fontSize:'0.6rem', display:'inline-block', transition:'transform 0.2s', transform: showPreview ? 'rotate(90deg)' : 'none' }}>▶</span>
          {fr ? "Voir ce que ton ami devra remplir" : "See what your friend will fill in"}
        </button>
        {showPreview && (() => {
          const firstName = (user?.name || '…').split(' ')[0];
          const DEMO_ROWS = fr ? [
            ['Évite le conflit',      'Très direct·e'       ],
            ['Aime la routine',       'Adore le changement' ],
            ['Très introverti·e',     'Très extraverti·e'   ],
            ['Centré·e sur lui-même', 'Très généreux·se'    ],
            ['Souvent distrait·e',    'Totalement présent·e'],
          ] : [
            ['Avoids conflict',    'Very assertive'   ],
            ['Loves routine',      'Loves change'     ],
            ['Very introverted',   'Very extroverted' ],
            ['Self-focused',       'Very generous'    ],
            ['Often distracted',   'Fully present'    ],
          ];
          return (
            <div style={{ marginTop:'0.5rem', background:'var(--surface,#1a1a1a)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.8rem 0.9rem' }}>
              {/* Pass 1 label */}
              <p style={{ fontSize:'0.65rem', fontWeight:700, color:'var(--gold)', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 0.6rem' }}>
                {fr ? `Comment est ${firstName} ?` : `How is ${firstName}?`}
              </p>

              {/* Column guides */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr repeat(7, 14px) 1fr', gap:'0 3px', marginBottom:'4px', alignItems:'center' }}>
                <span/>
                {[1,2,3,4,5,6,7].map(v => (
                  <span key={v} style={{ textAlign:'center', fontSize:'0.5rem', color:'rgba(128,128,128,0.4)' }}>{v}</span>
                ))}
                <span/>
              </div>

              {DEMO_ROWS.map(([l, r], i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr repeat(7, 14px) 1fr', gap:'0 3px', marginBottom: i < DEMO_ROWS.length - 1 ? '8px' : '0', alignItems:'center' }}>
                  <span style={{ fontSize:'0.62rem', color:'var(--muted)', textAlign:'right', paddingRight:'4px', lineHeight:1.3 }}>{l}</span>
                  {[1,2,3,4,5,6,7].map(v => (
                    <div key={v} style={{ width:14, height:14, borderRadius:'50%', background:'transparent', border:'1.5px solid rgba(128,128,128,0.25)' }}/>
                  ))}
                  <span style={{ fontSize:'0.62rem', color:'var(--muted)', paddingLeft:'4px', lineHeight:1.3 }}>{r}</span>
                </div>
              ))}

              {/* Pass 2 hint */}
              <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:'0.75rem 0 0', lineHeight:1.5, borderTop:'1px solid var(--border)', paddingTop:'0.6rem' }}>
                {fr
                  ? `→ Puis les mêmes axes du point de vue du partenaire idéal de ${firstName}.`
                  : `→ Then the same axes from the angle of ${firstName}'s ideal partner.`}
              </p>
            </div>
          );
        })()}
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'1.2rem' }}>
        {[
          [used,       fr ? 'utilisées' : 'used'],
          [total-used, fr ? 'en attente' : 'pending'],
        ].map(([n, label]) => (
          <div key={label} style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.7rem', textAlign:'center' }}>
            <p style={{ fontSize:'1.6rem', fontWeight:600, color:'var(--gold)', lineHeight:1 }}>{n}</p>
            <p style={{ fontSize:'0.65rem', color:'var(--muted)', marginTop:'0.2rem' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Sent invites list */}
      {loading ? (
        <p style={{ fontSize:'0.78rem', color:'var(--muted)', textAlign:'center', padding:'1rem' }}>Loading…</p>
      ) : invites.length === 0 ? (
        <p style={{ fontSize:'0.78rem', color:'var(--muted)', textAlign:'center', padding:'1.5rem 1rem', lineHeight:1.6 }}>
          {fr ? 'Aucune invitation envoyée pour l\'instant.' : 'No invitations sent yet.'}
        </p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {invites.map(inv => (
            <div key={inv.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--dark)', border:`1px solid ${inv.used_count > 0 ? 'rgba(78,201,148,0.3)' : 'var(--border)'}`, borderRadius:'var(--r-md)', padding:'0.65rem 0.85rem', gap:'0.5rem' }}>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ fontSize:'0.82rem', color: inv.used_count > 0 ? 'var(--muted)' : 'var(--white)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {inv.recipient_email}
                </div>
                <div style={{ fontSize:'0.62rem', color:'var(--muted)', marginTop:'0.15rem', display:'flex', gap:'0.5rem' }}>
                  {inv.invite_type === 'founder' && <span style={{ color:'#4EC994' }}>✦ Founder</span>}
                  {inv.used_count > 0
                    ? <span style={{ color:'#4EC994' }}>✓ {fr ? 'Utilisé' : 'Used'}</span>
                    : <span>{fr ? 'En attente' : 'Pending'}</span>
                  }
                </div>
              </div>
              {inv.used_count === 0 && (
                <button onClick={() => copyLink(inv.code)} style={{ flexShrink:0, padding:'0.3rem 0.75rem', background:copied===inv.code?'rgba(78,201,148,0.15)':'var(--gold-pale)', border:`1px solid ${copied===inv.code?'rgba(78,201,148,0.3)':'var(--gold-border)'}`, borderRadius:99, color:copied===inv.code?'#4EC994':'var(--gold)', fontSize:'0.68rem', cursor:'pointer', fontFamily:'Outfit', fontWeight:600 }}>
                  {copied === inv.code ? '✓' : (fr ? 'Copier' : 'Copy')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Profile preview modal ─────────────────────────────────────────────────
function ProfilePreviewModal({ user, lang, onClose }) {
  const photo = (user?.photos || [])[0];
  const interests = (user?.interests || []).slice(0, 6);
  const mood = user?.mood;
  const MoodIconComp = MOOD_ICONS_MAP[mood] || null;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem', backdropFilter:'blur(6px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:360, background:'var(--charcoal)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', overflow:'hidden', animation:'fadeInScale 0.25s ease' }}>
        <style>{`@keyframes fadeInScale{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}`}</style>

        {/* Card photo */}
        <div style={{ position:'relative', height:260, background:'var(--dark)', overflow:'hidden' }}>
          {photo
            ? <img src={photo} alt={user.name} style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'top' }}/>
            : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'5rem', color:'var(--gold)', opacity:0.15 }}>{(user.name||'?')[0]}</span>
              </div>}
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(transparent 40%, rgba(0,0,0,0.85))' }}/>
          <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'1rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
              <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, color:'#F2E8D0' }}>
                {user.name}{user.age ? `, ${user.age}` : ''}
              </span>
              {MoodIconComp && <MoodIconComp size={16} color="rgba(242,232,208,0.55)"/>}
              {user.is_founding_member && (
                <span style={{ fontSize:'0.6rem', padding:'2px 6px', borderRadius:99, background:'rgba(201,168,76,0.25)', border:'1px solid var(--gold-border)', color:'var(--gold)', fontWeight:600 }}>✦</span>
              )}
            </div>
            {(user.job || user.city) && (
              <p style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.55)', margin:'2px 0 0' }}>
                {[user.job, user.city].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </div>

        {/* Card body */}
        <div style={{ padding:'1rem' }}>
          {user.bio && (
            <p style={{ fontSize:'0.8rem', color:'var(--fg)', lineHeight:1.6, marginBottom:'0.75rem', fontStyle:'italic', opacity:0.85 }}>
              "{user.bio}"
            </p>
          )}
          {interests.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem', marginBottom:'0.75rem' }}>
              {interests.map(i => (
                <span key={i} style={{ fontSize:'0.68rem', padding:'3px 9px', borderRadius:99, background:'var(--gold-pale)', border:'1px solid var(--gold-border)', color:'var(--gold)' }}>
                  {i.replace(/^interest\./i,'')}
                </span>
              ))}
            </div>
          )}
          <p style={{ fontSize:'0.65rem', color:'var(--muted)', textAlign:'center', marginTop:'0.5rem' }}>
            {lang === 'fr' ? 'Aperçu tel que les autres vous voient' : 'Preview as others see you'}
          </p>
        </div>

        <div style={{ padding:'0 1rem 1rem' }}>
          <button onClick={onClose} style={{ width:'100%', padding:'0.7rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.8rem', cursor:'pointer' }}>
            {lang === 'fr' ? 'Fermer' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Amis tab — portrait (ratings) + invitation, with internal sub-tabs ───────
// Both sub-tabs require feature_friend_rating because they describe / surface
// the friend rating mechanic. If the flag is OFF, the whole Amis tab is hidden
// upstream (see profileTabs filter); this guard double-checks just in case.
function AmisTab({ user, tr, lang, features }) {
  const fr = lang === 'fr';
  const ratingOn = features?.friend_rating !== false;
  const AMIS_TABS = [
    ...(ratingOn && features?.profile_tab_ratings !== false ? [{ id:'portrait',   label: fr ? 'Portrait'   : 'Portrait',   icon:'◉', color:'#3A72C0',     bg:'rgba(58,114,192,0.18)'  }] : []),
    ...(ratingOn && features?.profile_tab_invite  !== false ? [{ id:'invitation', label: fr ? 'Invitation' : 'Invitation', icon:'✦', color:'var(--gold)', bg:'rgba(201,168,76,0.18)'  }] : []),
  ];
  if (AMIS_TABS.length === 0) {
    return (
      <div style={{ padding:'2.4rem 1.4rem', textAlign:'center', color:'var(--muted)' }}>
        <p style={{ fontSize:'0.85rem', fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic' }}>
          {fr ? 'Cette section est désactivée pour le moment.' : 'This section is disabled for now.'}
        </p>
      </div>
    );
  }
  const [sub, setSub] = useState(AMIS_TABS[0]?.id || 'portrait');
  return (
    <div>
      {AMIS_TABS.length > 1 && (
        <div style={{ display:'flex', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:3, margin:'0.9rem 1rem 0' }}>
          {AMIS_TABS.map(t => {
            const active = sub === t.id;
            return (
              <button key={t.id} onClick={() => setSub(t.id)} style={{
                flex:1, padding:'0.45rem 0.3rem',
                background: active ? t.bg : 'transparent',
                border: active ? `1px solid ${t.color}55` : '1px solid transparent',
                borderRadius:'calc(var(--r-md) - 2px)', cursor:'pointer',
                transition:'all 0.15s', fontFamily:'Outfit',
              }}>
                <span style={{ fontSize:'0.58rem', fontWeight: active ? 600 : 400, letterSpacing:'0.06em', textTransform:'uppercase', color: active ? 'var(--white)' : 'var(--muted)', transition:'color 0.15s' }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      )}
      {sub === 'portrait'   && <RatingsTab user={user} tr={tr} lang={lang}/>}
      {sub === 'invitation' && <InviteTab  user={user} tr={tr} lang={lang}/>}
    </div>
  );
}

// ── Settings tab ───────────────────────────────────────────────────────────
function SettingsTab({ user, tr, lang, setLang, theme, setTheme, onLogout, onDeleteAccount, subsEnabled, setPage, onToggleIncognito, onTogglePause, onToggleAvailable, onActivateBoost, onCancelBoost, isNetworkFeatureEnabled, features, setCurrentUser }) {
  const [confirmDelete,   setConfirmDelete]   = useState(false);
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);

  return (
    <div style={{ padding:'1rem 1.1rem 5rem' }}>
      {/* Founding member badge */}
      {user?.is_founding_member && (
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.6rem 0.9rem', background:'rgba(201,168,76,0.1)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-lg)', marginBottom:'1rem' }}>
          <span style={{ fontSize:'1rem' }}>✦</span>
          <div>
            <p style={{ fontSize:'0.78rem', fontWeight:600, color:'var(--gold)', margin:0 }}>
              {tr('invite.founding_badge') || 'Founding Member'}
            </p>
            <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:0 }}>
              {lang === 'fr' ? 'Membre de la première vague BeeLove.' : 'Part of the first BeeLove wave.'}
            </p>
          </div>
        </div>
      )}

      {/* Language */}
      <SelectRow label={tr('nav.lang_label') || 'Language'} value={lang}
        onChange={setLang} options={[['en','English'],['fr','Français']]}/>

      {/* Theme */}
      <SelectRow label={tr('nav.theme_label') || 'Theme'} value={theme}
        onChange={setTheme} options={[['dark', tr('nav.theme_dark')||'Dark'],['light', tr('nav.theme_light')||'Light']]}/>

      {/* Incognito mode — gated by perk_incognito feature flag */}
      {features?.perk_incognito && (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 0.85rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', marginBottom:'0.6rem' }}>
        <div>
          <p style={{ fontSize:'0.82rem', color:'var(--white)', margin:'0 0 2px' }}>
            {lang === 'fr' ? 'Mode incognito' : 'Incognito mode'}
          </p>
          <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:0 }}>
            {lang === 'fr' ? 'Vous n\'apparaissez pas dans Discover' : 'You won\'t appear in Discover'}
          </p>
        </div>
        <button
          onClick={onToggleIncognito}
          style={{
            width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
            background: user?.incognito ? 'var(--gold)' : 'var(--border)',
            position:'relative', transition:'background 0.2s', flexShrink:0,
          }}
          aria-label="Toggle incognito"
        >
          <span style={{
            position:'absolute', top:3, left: user?.incognito ? 23 : 3,
            width:18, height:18, borderRadius:9, background:'#fff',
            transition:'left 0.2s', display:'block',
          }}/>
        </button>
      </div>
      )}

      {/* Pause profile */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 0.85rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', marginBottom:'0.9rem' }}>
        <div>
          <p style={{ fontSize:'0.82rem', color:'var(--white)', margin:'0 0 2px' }}>
            {lang === 'fr' ? 'Mettre en pause' : 'Pause profile'}
          </p>
          <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:0 }}>
            {lang === 'fr' ? 'Votre profil est masqué, vos matchs restent actifs' : 'Profile hidden, existing matches stay active'}
          </p>
        </div>
        <button
          onClick={onTogglePause}
          style={{
            width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
            background: user?.paused ? 'var(--red)' : 'var(--border)',
            position:'relative', transition:'background 0.2s', flexShrink:0,
          }}
          aria-label="Toggle pause"
        >
          <span style={{
            position:'absolute', top:3, left: user?.paused ? 23 : 3,
            width:18, height:18, borderRadius:9, background:'#fff',
            transition:'left 0.2s', display:'block',
          }}/>
        </button>
      </div>

      {/* Disponible ce soir */}
      {(() => {
        const isAvailable = user?.available_until && new Date(user.available_until) > new Date();
        return (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 0.85rem', background: isAvailable ? 'var(--green-pale)' : 'var(--dark)', border: isAvailable ? '1px solid var(--green-border)' : '1px solid var(--border)', borderRadius:'var(--r-md)', marginBottom:'0.9rem' }}>
            <div>
              <p style={{ fontSize:'0.82rem', color: isAvailable ? 'var(--green)' : 'var(--white)', margin:'0 0 2px' }}>
                {lang === 'fr' ? 'Disponible ce soir' : 'Available tonight'}
              </p>
              <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:0 }}>
                {isAvailable
                  ? (lang === 'fr' ? 'Votre statut expire dans 24h' : 'Status expires in 24h')
                  : (lang === 'fr' ? 'Signalez que vous êtes ouvert à une rencontre' : 'Signal you\'re open to meeting up')}
              </p>
            </div>
            <button
              onClick={onToggleAvailable}
              style={{
                width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
                background: isAvailable ? 'var(--green)' : 'var(--border)',
                position:'relative', transition:'background 0.2s', flexShrink:0,
              }}
              aria-label="Toggle available"
            >
              <span style={{
                position:'absolute', top:3, left: isAvailable ? 23 : 3,
                width:18, height:18, borderRadius:9, background:'#fff',
                transition:'left 0.2s', display:'block',
              }}/>
            </button>
          </div>
        );
      })()}

      {/* Boost ciblé — visible uniquement si BeeHive est actif sur le réseau */}
      {isNetworkFeatureEnabled('beehive') && (() => {
        const isBoosted = user?.hive_boost_until && new Date(user.hive_boost_until) > new Date();
        const BOOST_GROUPS = [
          { id:'active', label:'Actifs', color:'#7AAA58' },
          { id:'food', label:'Food & Wine', color:'#C9A84C' },
          { id:'creative', label:'Créatifs', color:'#A084C4' },
          { id:'mindful', label:'Mindful', color:'#9CA880' },
          { id:'intellectual', label:'Curieux', color:'#7AADCA' },
        ];
        const minsLeft = isBoosted ? Math.ceil((new Date(user.hive_boost_until) - new Date()) / 60000) : 0;
        return (
          <div style={{ padding:'0.75rem 0.85rem', background: isBoosted ? 'rgba(201,168,76,0.08)' : 'var(--dark)', border: isBoosted ? '1px solid rgba(201,168,76,0.35)' : '1px solid var(--border)', borderRadius:'var(--r-md)', marginBottom:'0.9rem' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.45rem' }}>
              <p style={{ fontSize:'0.82rem', color: isBoosted ? 'var(--gold)' : 'var(--white)', margin:0 }}>
                {lang === 'fr' ? 'Boost ciblé' : 'Targeted boost'}
              </p>
              {isBoosted && (
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                  <span style={{ fontSize:'0.65rem', color:'var(--gold)' }}>{minsLeft >= 60 ? `${Math.ceil(minsLeft/60)}h` : `${minsLeft}min`}</span>
                  <button onClick={onCancelBoost} style={{ fontSize:'0.6rem', padding:'2px 7px', background:'transparent', border:'1px solid rgba(255,255,255,0.15)', borderRadius:99, color:'var(--muted)', cursor:'pointer', fontFamily:'Outfit' }}>Stop</button>
                </div>
              )}
            </div>
            <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:'0 0 0.6rem' }}>
              {isBoosted
                ? (lang === 'fr' ? `Actif dans « ${user.hive_boost_group} » — 2h de visibilité prioritaire` : `Active in "${user.hive_boost_group}" — 2h priority visibility`)
                : (lang === 'fr' ? 'Apparaissez en premier dans un groupe BeeHive pendant 2h' : 'Appear first in a BeeHive group for 2 hours')}
            </p>
            {!isBoosted && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:'0.35rem' }}>
                {BOOST_GROUPS.map(g => (
                  <button key={g.id} onClick={() => onActivateBoost(g.id)}
                    style={{ fontSize:'0.65rem', padding:'0.28rem 0.7rem', background:`${g.color}14`, border:`1px solid ${g.color}44`, borderRadius:99, color:g.color, cursor:'pointer', fontFamily:'Outfit' }}>
                    {g.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Founder badge */}
      {user?.plan === 'founder' && (
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.6rem 0.9rem', background:'rgba(78,201,148,0.1)', border:'1px solid rgba(78,201,148,0.35)', marginBottom:'1rem' }}>
          <span style={{ fontSize:'1rem' }}>✦</span>
          <div>
            <p style={{ fontSize:'0.78rem', fontWeight:600, color:'#4EC994', margin:0 }}>
              {lang === 'fr' ? 'Accès Fondateur' : 'Founder Access'}
            </p>
            <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:0 }}>
              {lang === 'fr' ? 'Accès Royal · Fonctionnalités beta en avant-première.' : 'Royal-level access · Early access to beta features.'}
            </p>
          </div>
        </div>
      )}

      {/* Plan */}
      {subsEnabled && user?.plan !== 'founder' && (
        <div style={{ marginBottom:'0.9rem' }}>
          <p style={{ fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.1em', color:'var(--muted)', textTransform:'uppercase', marginBottom:'0.4rem' }}>
            {lang === 'fr' ? 'Mon plan' : 'My plan'}
          </p>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 0.85rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)' }}>
            <span style={{ fontSize:'0.85rem', color:'var(--white)', textTransform:'capitalize' }}>
              {user?.plan || 'Free'}
              {user?.trial_ends_at && new Date(user.trial_ends_at) > new Date() && (
                <span style={{ marginLeft:6, fontSize:'0.65rem', color:'var(--gold)' }}>
                  ({lang === 'fr' ? 'Essai jusqu\'au' : 'Trial until'} {new Date(user.trial_ends_at).toLocaleDateString()})
                </span>
              )}
            </span>
            {user?.plan === 'free' && (
              <button onClick={() => setPage('upgrade')} style={{ padding:'0.3rem 0.8rem', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:99, color:'var(--gold)', fontSize:'0.7rem', cursor:'pointer', fontFamily:'Outfit', fontWeight:600 }}>
                {tr('common.upgrade') || 'Upgrade'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Invitations remaining */}
      <div style={{ padding:'0.65rem 0.85rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', marginBottom:'0.9rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:'0.82rem', color:'var(--muted)' }}>
          {lang === 'fr' ? 'Invitations restantes' : 'Invitations remaining'}
        </span>
        <span style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--gold)' }}>
          {user?.invites_remaining ?? 25}
        </span>
      </div>

      {/* Security — phone verification (gated by sms_verification feature flag) */}
      {features?.sms_verification && (
        <div style={{ marginBottom:'0.9rem' }}>
          <p style={{ fontSize:'0.65rem', fontWeight:600, letterSpacing:'0.1em', color:'var(--muted)', textTransform:'uppercase', marginBottom:'0.4rem' }}>
            {lang === 'fr' ? 'Sécurité' : 'Security'}
          </p>
          <div style={{ padding:'0.65rem 0.85rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <p style={{ fontSize:'0.82rem', color:'var(--white)', margin:'0 0 2px' }}>
                  {lang === 'fr' ? 'Vérification téléphone' : 'Phone verification'}
                </p>
                <p style={{ fontSize:'0.65rem', color:'var(--muted)', margin:0 }}>
                  {user?.phone_verified
                    ? (lang === 'fr' ? `Vérifié · ${maskPhone(user.phone)}` : `Verified · ${maskPhone(user.phone)}`)
                    : (lang === 'fr' ? 'Ajoutez votre numéro pour renforcer votre profil' : 'Add your number to strengthen your profile')}
                </p>
              </div>
              {user?.phone_verified ? (
                <span style={{ fontSize:'0.6rem', padding:'0.15rem 0.5rem', background:'rgba(78,201,148,0.1)', border:'1px solid rgba(78,201,148,0.3)', borderRadius:99, color:'#4EC994', flexShrink:0 }}>✓ {lang === 'fr' ? 'Vérifié' : 'Verified'}</span>
              ) : (
                <button onClick={() => setShowPhoneVerify(v => !v)}
                  style={{ padding:'0.3rem 0.8rem', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:99, color:'var(--gold)', fontSize:'0.7rem', cursor:'pointer', fontFamily:'Outfit', fontWeight:600, flexShrink:0 }}>
                  {showPhoneVerify ? (lang === 'fr' ? 'Fermer' : 'Close') : (lang === 'fr' ? 'Vérifier' : 'Verify')}
                </button>
              )}
            </div>
            {showPhoneVerify && !user?.phone_verified && (
              <div style={{ marginTop:'0.75rem', borderTop:'1px solid var(--border)', paddingTop:'0.75rem' }}>
                <VerifyIdentity
                  mode="phone"
                  compact
                  onComplete={(phone) => {
                    setCurrentUser(u => ({ ...u, phone_verified: true, phone }));
                    setShowPhoneVerify(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log out */}
      <button onClick={onLogout} style={{ width:'100%', padding:'0.8rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.85rem', cursor:'pointer', marginBottom:'0.6rem' }}>
        {tr('auth.sign_out') || 'Sign out'}
      </button>

      {/* Delete account */}
      {!confirmDelete ? (
        <button onClick={() => setConfirmDelete(true)} style={{ width:'100%', padding:'0.6rem', background:'transparent', border:'none', color:'rgba(224,112,112,0.5)', fontFamily:'Outfit', fontSize:'0.75rem', cursor:'pointer' }}>
          {lang === 'fr' ? 'Supprimer mon compte' : 'Delete my account'}
        </button>
      ) : (
        <div style={{ background:'rgba(224,112,112,0.08)', border:'1px solid rgba(224,112,112,0.3)', borderRadius:'var(--r-md)', padding:'0.9rem', textAlign:'center' }}>
          <p style={{ fontSize:'0.78rem', color:'#E07070', marginBottom:'0.7rem' }}>
            {lang === 'fr' ? 'Cette action est irréversible.' : 'This cannot be undone.'}
          </p>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={() => setConfirmDelete(false)} style={{ flex:1, padding:'0.6rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', cursor:'pointer', fontFamily:'Outfit', fontSize:'0.78rem' }}>
              {tr('common.cancel') || 'Cancel'}
            </button>
            <button onClick={onDeleteAccount} style={{ flex:1, padding:'0.6rem', background:'rgba(224,112,112,0.15)', border:'1px solid rgba(224,112,112,0.4)', borderRadius:'var(--r-md)', color:'#E07070', cursor:'pointer', fontFamily:'Outfit', fontSize:'0.78rem' }}>
              {lang === 'fr' ? 'Supprimer' : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Profile component ────────────────────────────────────────────────
export default function Profile() {
  const tr = useTr();
  const {
    currentUser, setCurrentUser,
    lang, setLang,
    theme, setTheme,
    logout, deleteUserAccount,
    subsEnabled, setPage,
    toggleIncognito, togglePause, toggleAvailable,
    activateHiveBoost, cancelHiveBoost,
    isNetworkFeatureEnabled,
    features,
  } = useApp();

  const [tab, setTab] = useState('profile');

  const saveCore    = useCallback(async (fields) => {
    if (!currentUser?.id) return;
    setCurrentUser(u => ({ ...u, ...fields }));
    await dbUpdate.userCore(currentUser.id, fields);
  }, [currentUser?.id]);

  const saveProfile = useCallback(async (fields) => {
    if (!currentUser?.id) return;
    setCurrentUser(u => ({ ...u, ...fields }));
    await dbUpdate.userProfile(currentUser.id, fields);
  }, [currentUser?.id]);

  const savePhotos = useCallback(async (photos) => {
    if (!currentUser?.id) return;
    setCurrentUser(u => ({ ...u, photos }));
    await dbUpdate.userProfile(currentUser.id, { photos });
  }, [currentUser?.id]);

  const handleLogout = async () => {
    await logout?.();
  };

  const handleDeleteAccount = async () => {
    await deleteUserAccount?.();
  };

  if (!currentUser) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ color:'var(--muted)', fontFamily:'Outfit' }}>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:'var(--black)', fontFamily:'Outfit' }}>
      {/* Header */}
      <div style={{ position:'relative', height:200, background:'var(--charcoal)', overflow:'hidden' }}>
        {currentUser.photos?.[0]
          ? <img src={currentUser.photos[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'top' }}/>
          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'5rem', color:'var(--gold)', opacity:0.15 }}>
                {(currentUser.name||'?')[0]}
              </span>
            </div>}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(transparent 30%, rgba(0,0,0,0.85))' }}/>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'1rem 1.2rem' }}>
          <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between' }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:400, color:'#F2E8D0', margin:0 }}>
                  {currentUser.name}{currentUser.age ? `, ${currentUser.age}` : ''}
                </h1>
                <FoundingBadge user={currentUser} size="md" lang={lang}/>
              </div>
              <p style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.55)', margin:0 }}>
                {[currentUser.job, currentUser.city].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
              {(currentUser.votes || 0) > 0 && (
                <div style={{ textAlign:'right' }}>
                  <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', fontWeight:400, color:'var(--gold)', lineHeight:1, margin:0 }}>{currentUser.votes}</p>
                  <p style={{ fontSize:'0.6rem', color:'rgba(255,255,255,0.4)', margin:'2px 0 0' }}>
                    {currentUser.votes === 1 ? 'friend' : 'friends'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <TabBar tab={tab} setTab={setTab} tr={tr} features={features}/>

      {tab === 'profile'  && <ProfileTab  user={currentUser} lang={lang} tr={tr} onSaveCore={saveCore} onSaveProfile={saveProfile} onSavePhotos={savePhotos}/>}
      {tab === 'hive'     && <HiveTab     user={currentUser} lang={lang} tr={tr} onSaveCore={saveCore} onSaveProfile={saveProfile}/>}
      {tab === 'amis'     && <AmisTab     user={currentUser} tr={tr} lang={lang} features={features}/>}
      {tab === 'settings' && <SettingsTab user={currentUser} tr={tr} lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} onLogout={handleLogout} onDeleteAccount={handleDeleteAccount} subsEnabled={subsEnabled} setPage={setPage} onToggleIncognito={toggleIncognito} onTogglePause={togglePause} onToggleAvailable={toggleAvailable} onActivateBoost={activateHiveBoost} onCancelBoost={cancelHiveBoost} isNetworkFeatureEnabled={isNetworkFeatureEnabled} features={features} setCurrentUser={setCurrentUser}/>}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
